import React from 'react';
import { ShieldCheck, Flag, Lock } from 'lucide-react';
import { cn } from '../../lib/utils';
import japetoLogoLight from '../../assets/images/japeto-logo-light.svg';

interface AuthLayoutProps {
  children: React.ReactNode;
  belowCard?: React.ReactNode;
}

const TRUST_BADGES = [
  { icon: ShieldCheck, label: 'GDPR compliant' },
  { icon: Flag, label: 'UK hosted' },
  { icon: Lock, label: 'Encrypted at rest' }
] as const;

function BackgroundDecorations(): React.ReactElement {
  return (
    <div
      aria-hidden
      className='pointer-events-none absolute inset-0 overflow-hidden'
    >
      <div className='absolute -left-[8%] -top-[14%] h-[640px] w-[640px] rounded-full bg-auth-glow-cyan animate-auth-drift-1 will-change-transform' />
      <div className='absolute -bottom-[20%] -right-[12%] h-[720px] w-[720px] rounded-full bg-auth-glow-gold animate-auth-drift-2 will-change-transform' />
      <div className='absolute -bottom-[10%] left-[30%] h-[460px] w-[460px] rounded-full bg-auth-glow-teal animate-auth-drift-3 will-change-transform' />
      <div className='absolute left-[8%] top-[12%] h-[220px] w-[220px] animate-auth-drift-3'>
        <div className='h-full w-full rounded-full border-[1.5px] border-dashed border-white/[0.14] animate-auth-ring-spin' />
      </div>
      <div className='absolute bottom-[10%] right-[10%] h-[320px] w-[320px] animate-auth-drift-1'>
        <div className='h-full w-full rounded-full border-[1.5px] border-dashed border-white/10 animate-auth-ring-spin-reverse' />
      </div>
      <div className='absolute right-[16%] top-[18%] hidden h-3.5 w-3.5 rounded-full bg-auth-gold animate-auth-float sm:block' />
      <div className='absolute bottom-[22%] left-[14%] hidden h-2.5 w-2.5 rounded-full bg-auth-cyan animate-auth-float [animation-delay:1.5s] sm:block' />
      <div className='absolute left-[24%] top-[30%] hidden h-[7px] w-[7px] rounded-full bg-white/50 animate-auth-float [animation-delay:0.8s] sm:block' />
      <div className='absolute bottom-[30%] right-[26%] hidden h-2 w-2 rounded-full bg-auth-gold/70 animate-auth-float [animation-delay:2.2s] sm:block' />
    </div>
  );
}

function AuthLayout({
  children,
  belowCard
}: Readonly<AuthLayoutProps>): React.ReactElement {
  return (
    <div className='relative flex min-h-screen flex-col items-center overflow-y-auto overflow-x-hidden bg-auth-gradient px-5 py-10'>
      <BackgroundDecorations />
      <div className='relative my-auto flex w-full flex-col items-center'>
        <img
          src={japetoLogoLight}
          alt='Japeto'
          className='mb-2.5 h-20 w-auto'
        />
        <p className='mb-[30px] text-base text-auth-mist'>
          AI assistants for charities &amp; healthcare
        </p>
        <div
          className={cn(
            'w-[508px] max-w-[calc(100vw-40px)] rounded-2xl bg-white px-6 py-8 shadow-auth-card sm:px-11 sm:py-10',
            '[&_label]:mb-1.5 [&_label]:text-[13px] [&_label]:font-bold',
            '[&_button[aria-label$="password"]]:top-[19px] [&_button[aria-label$="password"]]:h-[46px]'
          )}
        >
          {children}
        </div>
        {belowCard && <div className='mt-[26px]'>{belowCard}</div>}
        <div className='mt-[30px] flex flex-wrap items-center justify-center gap-7 text-[13px] text-auth-mist'>
          {TRUST_BADGES.map(({ icon: Icon, label }) => (
            <span key={label} className='flex items-center gap-[7px]'>
              <Icon className='h-[17px] w-[17px] text-auth-cyan' aria-hidden />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
